// ============================================
// App.js — Root Application Component
// Web Portal: Admin & Dispatcher only
// User & Driver access via mobile PWA
// ============================================

import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Provider, useDispatch, useSelector } from 'react-redux';
import store from './app/store';
import { loadUser, tryRefreshOnStartup } from './redux/slices/authSlice';
import socketService from './services/socket.js';
import useAutoLogout from './hooks/useAutoLogout';

// Auth Pages
import AdminLogin from './pages/admin/AdminLogin';
import DispatcherLogin from './pages/dispatcher/DispatcherLogin';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';

// Public Pages
// ... (rest of imports)

// Public Pages
import PublicTracking from './pages/user/PublicTracking';

// Dashboards
import AdminDashboard from './pages/admin/AdminDashboard';
import DispatcherDashboard from './pages/dispatcher/DispatcherDashboard';
import DriverDashboard from './pages/driver/DriverDashboard';
import MyRoutes from './pages/driver/MyRoutes';
import Compliance from './pages/driver/Compliance';
import AvailableJobs from './pages/driver/AvailableJobs';
import DriverSettings from './pages/driver/Settings';
import MissionHistory from './pages/driver/MissionHistory';

// PA Pages
import PADashboard from './pages/pa/PADashboard';
import PADocuments from './pages/pa/PADocuments';
import PAProfile from './pages/pa/PAProfile';
import PASettings from './pages/pa/PASettings';
import UserDashboard from './pages/user/UserDashboard';
import BookRide from './pages/user/BookRide';
import MyTrips from './pages/user/MyTrips';
import UserSettings from './pages/user/Settings';
import DispatcherDrivers from './pages/dispatcher/DispatcherDrivers';
import DispatcherPAs from './pages/dispatcher/DispatcherPAs';
import DispatcherRoutes from './pages/dispatcher/DispatcherRoutes';
import DispatcherVehicles from './pages/dispatcher/DispatcherVehicles';
import DispatcherChildren from './pages/dispatcher/DispatcherChildren';
import {
  LiveMap, ActiveJobs, JobReassignment, TrackingLinks, Alerts, JobHistory
} from './pages/dispatcher/DispatcherOperationalPages';
import DispatcherSettings from './pages/dispatcher/Settings';

// Admin CRUD Pages (Phase 3)
import SchoolManagement from './pages/admin/SchoolManagement';
import ChildManagement from './pages/admin/ChildManagement';
import DriverManagement from './pages/admin/DriverManagement';
import RouteManagement from './pages/admin/RouteManagement';
import VehicleManagement from './pages/admin/VehicleManagement';
import DocumentManagement from './pages/admin/DocumentManagement';
import PAManagement from './pages/admin/PAManagement';
import SchoolJobs from './pages/admin/SchoolJobs';
import PrivateJobs from './pages/admin/PrivateJobs';
import Reports from './pages/admin/Reports';
import Settings from './pages/admin/Settings';
import BlockedMessages from './pages/admin/BlockedMessages';

// Route Guard
import ProtectedRoute from './routes/ProtectedRoute';

// App Imports
import { ToastProvider, useToast } from './context/ToastContext';
import { ThemeProvider } from './context/ThemeContext';

// ── Smart Redirect: checks for existing tokens ──
const SmartRedirect = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  
  // If already authenticated, redirect to role dashboard
  if (isAuthenticated && user) {
    const routes = { admin: '/admin', dispatcher: '/dispatcher', driver: '/driver', user: '/user', pa: '/pa' };
    return <Navigate to={routes[user.role] || '/login'} replace />;
  }
  
  // Check if any token exists in localStorage
  const tokenKeys = ['driver_token', 'pa_token', 'token', 'dispatcher_token', 'admin_token'];
  for (const key of tokenKeys) {
    if (localStorage.getItem(key)) {
      const roleMap = { token: '/user', driver_token: '/driver', pa_token: '/pa', admin_token: '/admin', dispatcher_token: '/dispatcher' };
      return <Navigate to={roleMap[key]} replace />;
    }
  }
  
  // Use last_role for intelligent redirect to appropriate login page
  const lastRole = localStorage.getItem('last_role');
  if (lastRole === 'admin') return <Navigate to="/admin/login" replace />;
  if (lastRole === 'dispatcher') return <Navigate to="/dispatcher/login" replace />;
  if (lastRole === 'driver') return <Navigate to="/driver/login" replace />;
  
  return <Navigate to="/login" replace />;
};

// ── App Content (needs Redux context) ───────
const AppContent = () => {
  useAutoLogout(); // Global auto-logout watcher
  // ... existing code ...
  const dispatch = useDispatch();
  const { token, user: reduxUser } = useSelector((state) => state.auth);

  const toast = useToast();

  // Store toast ref so the listener callback doesn't go stale
  const toastRef = React.useRef(toast);
  React.useEffect(() => { toastRef.current = toast; }, [toast]);

  // Load user profile when token is available
  useEffect(() => {
    if (token) {
      // Access token exists — validate it by fetching profile
      dispatch(loadUser());
      // Try immediate socket connect from localStorage (fast path)
      let user = null;
      try {
          const userStr = localStorage.getItem('user');
          if (userStr && userStr !== 'undefined') {
              user = JSON.parse(userStr);
          }
      } catch (e) {
          console.warn('Error parsing user from localStorage', e);
      }
      if (user) {
        socketService.connect(user.role, user._id);
      }
    } else {
      // No access token — check if a refresh token exists for THIS portal only
      // (no cross-portal fallback — each portal is strictly isolated)
      const path = window.location.pathname;
      let refreshKey = 'refreshToken';
      if (path.startsWith('/admin')) refreshKey = 'admin_refreshToken';
      else if (path.startsWith('/dispatcher')) refreshKey = 'dispatcher_refreshToken';
      else if (path.startsWith('/driver')) refreshKey = 'driver_refreshToken';
      else if (path.startsWith('/pa')) refreshKey = 'pa_refreshToken';

      if (localStorage.getItem(refreshKey)) {
        // Refresh token exists — try to silently restore the session (Step 68)
        dispatch(tryRefreshOnStartup());
      } else {
        // Both tokens gone (Step 70) — isLoading stays false, ProtectedRoute redirects
        socketService.disconnect();
      }
    }
  }, [dispatch, token]);

  // Ensure socket connects once Redux user is available (covers the case
  // where localStorage 'user' was missing/stale and loadUser() resolved it)
  useEffect(() => {
    if (token && reduxUser && reduxUser.role && reduxUser._id) {
      socketService.connect(reduxUser.role, reduxUser._id);
    }
  }, [token, reduxUser]);

  // Separate effect for the global notification listener — runs only once
  useEffect(() => {
    const handleNotification = (notif) => {
      if (notif.type === 'mission_issue') {
        toastRef.current.warning(notif.message, 10000);
      } else {
        toastRef.current.info(notif.message);
      }
    };
    
    const handleForceLogout = () => {
      toastRef.current.error('Session terminated remotely or logged out from another window.');
      const lastRole = localStorage.getItem('last_role');
      socketService.disconnect();
      localStorage.clear();
      if (lastRole) localStorage.setItem('last_role', lastRole);
      
      const loginPaths = { admin: '/admin/login', dispatcher: '/dispatcher/login', driver: '/driver/login', pa: '/login', user: '/login' };
      window.location.href = loginPaths[lastRole] || '/login';
    };

    socketService.on('notification:new', handleNotification);
    socketService.on('auth:force_logout', handleForceLogout);
    
    return () => {
      socketService.off('notification:new', handleNotification);
      socketService.off('auth:force_logout', handleForceLogout);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Routes>
      {/* ═══════════════════════════════════════════
          ADMIN — /admin/login → /admin (dashboard)
         ═══════════════════════════════════════════ */}
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute allowedRoles={['admin']} loginPath="/admin/login">
            <Routes>
              <Route index element={<AdminDashboard />} />
              <Route path="schools" element={<SchoolManagement />} />
              <Route path="children" element={<ChildManagement />} />
              <Route path="drivers" element={<DriverManagement />} />
              <Route path="pas" element={<PAManagement />} />
              <Route path="routes" element={<RouteManagement />} />
              <Route path="vehicles" element={<VehicleManagement />} />
              <Route path="documents" element={<DocumentManagement />} />
              <Route path="school-jobs" element={<SchoolJobs />} />
              <Route path="private-jobs" element={<PrivateJobs />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<Settings />} />
              <Route path="blocked-messages" element={<BlockedMessages />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* ═══════════════════════════════════════════
          DISPATCHER — /dispatcher/login → /dispatcher
         ═══════════════════════════════════════════ */}
      <Route path="/dispatcher/login" element={<DispatcherLogin />} />
      <Route
        path="/dispatcher/*"
        element={
          <ProtectedRoute allowedRoles={['dispatcher']} loginPath="/dispatcher/login">
            <Routes>
              <Route index element={<DispatcherDashboard />} />
              <Route path="drivers" element={<DispatcherDrivers />} />
              <Route path="pas" element={<DispatcherPAs />} />
              <Route path="routes" element={<DispatcherRoutes />} />
              <Route path="vehicles" element={<DispatcherVehicles />} />
              <Route path="children" element={<DispatcherChildren />} />
              <Route path="live-map" element={<LiveMap />} />
              <Route path="active-jobs" element={<ActiveJobs />} />
              <Route path="private-jobs" element={<PrivateJobs />} />
              <Route path="reassign" element={<JobReassignment />} />
              <Route path="tracking-links" element={<TrackingLinks />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="history" element={<JobHistory />} />
              <Route path="reports" element={<Reports />} />
              <Route path="settings" element={<DispatcherSettings />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* ═══════════════════════════════════════════
          DRIVER — /driver/login → /driver
         ═══════════════════════════════════════════ */}
      <Route path="/driver/login" element={<LoginPage />} />
      <Route path="/driver/register" element={<RegisterPage />} />
      <Route
        path="/driver/*"
        element={
          <ProtectedRoute allowedRoles={['driver']} loginPath="/login">
            <Routes>
              <Route index element={<DriverDashboard />} />
              <Route path="routes" element={<MyRoutes />} />
              <Route path="documents" element={<Compliance />} />
              <Route path="available-jobs" element={<AvailableJobs />} />
              <Route path="history" element={<MissionHistory />} />
              <Route path="settings" element={<DriverSettings />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* ═══════════════════════════════════════════
          PA — /login (PA toggle) → /pa
         ═══════════════════════════════════════════ */}
      <Route
        path="/pa/*"
        element={
          <ProtectedRoute allowedRoles={['pa']} loginPath="/login">
            <Routes>
              <Route index element={<PADashboard />} />
              <Route path="documents" element={<PADocuments />} />
              <Route path="profile" element={<PAProfile />} />
              <Route path="settings" element={<PASettings />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* ═══════════════════════════════════════════
          USER — /login → /user
         ═══════════════════════════════════════════ */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      <Route
        path="/user/*"
        element={
          <ProtectedRoute allowedRoles={['user']} loginPath="/login">
            <Routes>
              <Route index element={<UserDashboard />} />
              <Route path="book" element={<BookRide />} />
              <Route path="trips" element={<MyTrips />} />
              <Route path="settings" element={<UserSettings />} />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* Public Tracking Link */}
      <Route path="/track/:token" element={<PublicTracking />} />

      {/* Root & catch-all → Smart redirect based on existing token */}
      <Route path="/" element={<SmartRedirect />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

// ── Root App Component ──────────────────────
const App = () => {
  return (
    <ThemeProvider>
      <Provider store={store}>
        <ToastProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </ToastProvider>
      </Provider>
    </ThemeProvider>
  );
};

export default App;
